import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const MyLeaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);

  const [leaveType, setLeaveType] = useState('Leave');
  const [startDate, setStartDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchLeaves();
  }, []);

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('employeeToken');
      const res = await fetch('/api/employee/leaves', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await res.json();
      if (result.success) {
        setLeaves(result.data || []);
      } else {
        setError(result.error || 'Failed to load leave records');
      }
    } catch (err) {
      setError(err.message || 'Error loading leaves');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    if (!startDate) {
      setError('Date is required');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('employeeToken');
      const res = await fetch('/api/employee/leaves', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          leave_type: leaveType,
          start_date: startDate,
          reason: reason
        })
      });

      const result = await res.json();
      if (result.success) {
        setShowApplyModal(false);
        setStartDate('');
        setReason('');
        fetchLeaves();
      } else {
        setError(result.error || 'Failed to submit leave request');
      }
    } catch (err) {
      setError(err.message || 'Error submitting leave');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status) => {
    const s = (status || 'pending').toLowerCase();
    if (s === 'approved') {
      return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">Approved</span>;
    } else if (s === 'rejected') {
      return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-500/10 text-rose-600 border border-rose-500/20">Rejected</span>;
    }
    return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-600 border border-amber-500/20">Pending Review</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header Bar */}
      <div className="bg-card border border-border rounded-xl p-5 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-foreground">My Leave Requests</h2>
          <p className="text-xs text-muted-foreground">Submit leave applications for manager approval</p>
        </div>

        <Button
          onClick={() => setShowApplyModal(true)}
          className="bg-teal-600 hover:bg-teal-700 text-white font-semibold"
          iconName="Plus"
          iconPosition="left"
        >
          Apply for Leave
        </Button>
      </div>

      {/* Leave List Table */}
      {loading ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center shadow-sm">
          <Icon name="Loader2" size={28} className="animate-spin text-teal-600 mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">Loading leave requests...</p>
        </div>
      ) : error ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center text-rose-500 font-semibold shadow-sm">
          {error}
        </div>
      ) : leaves.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground shadow-sm">
          <Icon name="Calendar" size={36} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm font-semibold">No leave requests submitted yet.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50 border-b border-border text-xs font-semibold text-muted-foreground uppercase">
                <tr>
                  <th className="py-3 px-4">Leave Date</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Reason</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Applied On</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {leaves.map((l) => (
                  <tr key={l.id} className="hover:bg-muted/30 transition-colors">
                    <td className="py-3.5 px-4 font-semibold text-foreground">{l.leave_date}</td>
                    <td className="py-3.5 px-4 font-medium text-foreground">{l.leave_type || 'Leave'}</td>
                    <td className="py-3.5 px-4 text-muted-foreground max-w-xs truncate">{l.reason || 'N/A'}</td>
                    <td className="py-3.5 px-4">{statusBadge(l.status)}</td>
                    <td className="py-3.5 px-4 text-xs text-muted-foreground">{l.created_at ? l.created_at.split(' ')[0] : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Apply Leave Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-5 border-b border-border bg-muted/30 flex items-center justify-between">
              <h3 className="text-base font-bold text-foreground">Apply for Leave</h3>
              <button onClick={() => setShowApplyModal(false)} className="text-muted-foreground hover:text-foreground p-1.5">
                <Icon name="X" size={18} />
              </button>
            </div>

            <form onSubmit={handleApplyLeave} className="p-5 space-y-4 text-sm">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Leave Type</label>
                <Select
                  options={[
                    { value: 'Leave', label: 'Unpaid Leave (L)' },
                    { value: 'Paid Leave', label: 'Paid Leave (PL)' },
                    { value: 'Half Day', label: 'Half Day (HD)' }
                  ]}
                  value={leaveType}
                  onChange={(val) => setLeaveType(typeof val === 'object' ? val?.value || val?.target?.value : val)}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Date</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Reason</label>
                <textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason for leave request..."
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs focus:ring-1 focus:ring-teal-500"
                />
              </div>

              <div className="pt-3 border-t border-border flex items-center justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowApplyModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                  {submitting ? "Submitting..." : "Submit Application"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyLeaves;
